<template>
  <section class="xiaomi-stage">
    <CdnImage
      class="xiaomi-stage__background"
      alt="Xiaomi"
      src="/content/content-images/landingpages/xiaomi/stage/182c27ff7ac1e1b70d356dfa83f959ed/buehne-s@2x.jpg"
      src-sm="/content/content-images/landingpages/xiaomi/stage/d75329771681b5527890461ae16159a3/buehne-m@2x.jpg"
      src-lg="/content/content-images/landingpages/xiaomi/stage/1dd5eea210a2e4a5d0dbcbe4eff72bd8/buehne-l@2x.jpg"
    />
    <GridContainer
      padding
      class="xiaomi-stage__content-wrapper"
    >
      <div class="xiaomi-stage__logo">
        <SvgIcon
          class="xiaomi-stage__logo-icon"
          :image="xiaomiLogo"
        />
      </div>
      <Heading
        tag="h1"
        font-weight="ultra"
        size="xl"
        text-uppercase
        align="center"
        class="xiaomi-stage__heading"
      >
        {{ stageContent.mainTitle }}
      </Heading>
      <Paragraph
        is-size-big
        align="center"
        class="xiaomi-stage__description"
      >
        {{ stageContent.description }}
      </Paragraph>
      <ul
        v-if="stageContent.products"
        class="xiaomi-stage__list"
      >
        <li
          v-for="(item, index) in stageContent.products"
          :key="`product-${index}`"
          :ref="item.ref"
          class="xiaomi-stage__product"
          :class="`xiaomi-stage__product--${item.ref}`"
        >
          <CdnImage
            class="xiaomi-stage__image"
            :src="item.src"
            :alt="item.alt"
            sizes="xs:50vw sm:520px"
          >
            <PlusIconOpener
              v-if="item.plusIcon"
              class="xiaomi-stage__plus-icon"
              :class="`xiaomi-stage__plus-icon--${item.ref}`"
              :title="item.plusIcon.title"
              :label="item.plusIcon.label"
              :xs-open-left="item.plusIcon.xsOpenLeft"
              :sm-open-left="item.plusIcon.smOpenLeft"
              :md-open-left="item.plusIcon.mdOpenLeft"
              :anker-link="item.ref"
              @go-to-section="emitGoToSection"
            />
          </CdnImage>
        </li>
      </ul>
    </GridContainer>
  </section>
</template>

<script>
import xiaomiLogo from '@/assets/images/mi-logo.svg';
import { GridContainer, Heading, Paragraph } from '@i22-td-smarthome/component-library';
import ScrollAnimation from '@i22/scroll-animation';
import PlusIconOpener from '@/components/plus-icon-opener.vue';
import CdnImage from '@/components/cdn-image.vue';

const imagePath = '/content/content-images/landingpages/xiaomi/stage';

const STAGE_DATA = {
  mainTitle: 'Xiaomi: innovation für jeden moment',
  description:
    'Das Xiaomi-Sortiment steht für Innovation in jedem Moment – intelligent vernetzt und bereit, dir das Leben zu erleichtern. Egal, ob bei der Arbeit oder in der Freizeit, unsere Geräte sind darauf ausgerichtet, jeden Moment zu einem einzigartigen Erlebnis zu machen. Willkommen in der Themenwelt von Xiaomi, wo du deine Innovation für den Alltag findest.',
  background: {
    s: `${imagePath}/182c27ff7ac1e1b70d356dfa83f959ed/buehne-s@2x.jpg`,
    m: `${imagePath}/d75329771681b5527890461ae16159a3/buehne-m@2x.jpg`,
    l: `${imagePath}/1dd5eea210a2e4a5d0dbcbe4eff72bd8/buehne-l@2x.jpg`,
  },
  products: [
    {
      alt: 'Xiaomi Scooter 4-lite',
      src: `${imagePath}/8c79dd2635b70e509e82dc6b99b2130e/scooter-lite-4-buehne@2x.png`,
      ref: 'scooterLite4',
      positionY: {
        desktop: 48,
        mobile: 15,
      },
      plusIcon: {
        title: 'Xiaomi Scooter 4-lite',
        label: 'Mehr erfahren',
      },
    },
    {
      alt: 'Redmi Buds 5 Pro',
      src: `${imagePath}/7cb1cf5dda5a51a2dd266f7170f68425/ear-buds-buehne@2x.png`,
      ref: 'redmiBuds5',
      positionY: {
        desktop: 48,
        mobile: 10,
      },
      plusIcon: {
        title: 'Redmi Buds 5 Pro',
        label: 'Mehr erfahren',
        xsOpenLeft: true,
        smOpenLeft: false,
      },
    },
    {
      alt: 'AirFryer 6.5',
      src: `${imagePath}/7867c22d30504879da72198e4fbef6e7/air-fryer-buehne@2x.png`,
      ref: 'airFryer',
      positionY: {
        desktop: 48,
        mobile: 30,
      },
      plusIcon: {
        title: 'AirFryer 6.5',
        label: 'Mehr erfahren',
        smOpenLeft: true,
        mdOpenLeft: true,
      },
    },
    {
      alt: 'Watch 2 Pro',
      src: `${imagePath}/939e67fcaeab8af3d957a441139c033c/watch-buehne@2x.png`,
      ref: 'watchPro',
      positionY: {
        desktop: 48,
        mobile: 40,
      },
      plusIcon: {
        title: 'Watch 2 Pro',
        label: 'Mehr erfahren',
        xsOpenLeft: true,
        smOpenLeft: true,
        mdOpenLeft: true,
      },
    },
  ],
};

export default {
  name: 'XiaomiStage',
  components: { GridContainer, Heading, PlusIconOpener, Paragraph, CdnImage },
  mixins: [ScrollAnimation],
  emits: ['go-to-section'],
  data() {
    return {
      stageContent: STAGE_DATA,
      xiaomiLogo,
    };
  },
  mounted() {
    this.initAnimations();
  },
  methods: {
    emitGoToSection(anker) {
      this.$emit('go-to-section', anker);
    },
    initAnimations() {
      const refsCollection = this.stageContent.products.map((item) => ({
        ref: item.ref,
        positionY: item.positionY,
      }));

      refsCollection.forEach((item, index) => {
        this.createMediaScrollTrigger({
          '(max-width: 639px)': () => {
            this.createScrollTrigger({
              trigger: item.ref.el,
              start: 'top top+=700px',
              end: 'bottom center',
              scrub: index,
              ease: 'expo.inOut',
              markers: false,
              animation: this.createTweenFrom(`.xiaomi-stage__product--${item.ref}`, {
                yPercent: item.positionY.mobile,
              }),
            });
          },
          '(min-width: 640px)': () => {
            this.createScrollTrigger({
              trigger: item.ref.el,
              start: 'top center',
              end: 'bottom-=250 top+=300',
              scrub: index + 2,
              ease: 'expo.inOut',
              markers: false,
              animation: this.createTweenFrom(`.xiaomi-stage__product--${item.ref}`, {
                yPercent: item.positionY.desktop,
              }),
            });
          },
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.xiaomi-stage {
  position: relative;
  padding-top: $grid-base * 2;
  padding-bottom: 8 * $grid-base;

  @include mq($mq-sm) {
    margin-bottom: 150px;
    min-height: 800px;
    padding-bottom: 0;
  }

  @include mq($mq-md) {
    padding-bottom: $grid-base * 3;
    padding-top: $grid-base * 4;
    min-height: 680px;
  }

  &__background {
    z-index: -1;
    background-color: color(white);
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    :deep(img) {
      object-fit: cover;
      width: 100%;
      min-width: 100%;
      min-height: 100%;
      aspect-ratio: 1;

      @include mq($mq-sm) {
        aspect-ratio: 16 / 10;
      }
      @include mq($mq-lg) {
        aspect-ratio: 4 / 1;
      }
    }
  }

  &__heading {
    max-width: 800px;
    margin: 0 auto;
    padding-bottom: 0.5em;
    padding-top: 0.5em;

    @include mq($mq-md) {
      margin: 0;
      text-align: left;
    }
  }

  &__description {
    max-width: 980px;
    margin: 0 auto;

    @include mq($mq-md) {
      margin: 0;
      text-align: left;
      max-width: 40%;
    }
  }

  &__list {
    margin: 0 auto;
    @include mq(false, $mq-sm - 1) {
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      align-content: space-between;
      max-width: 500px;
    }
    @include mq($mq-sm) {
      position: relative;
      top: $grid-base;
      max-width: 800px;
      height: 480px;
    }
    @include mq($mq-md) {
      position: absolute;
      width: 70%;
      right: 0;
      top: 35%;
    }
  }

  &__content-wrapper {
    position: relative;
  }

  &__logo {
    text-align: right;
  }

  &__logo-icon {
    height: 48px;
    width: auto;
    @include mq($mq-sm) {
      height: 60px;
    }
  }

  &__image {
    position: relative;
  }
  // Position Product List Item
  &__product {
    list-style: none;

    @include mq(false, $mq-sm - 1) {
      width: 100%;
      display: flex;
    }

    @include mq($mq-sm) {
      position: absolute;
      top: initial;
    }

    :deep(img) {
      display: block;
      width: 100%;
    }

    &--scooterLite4 {
      :deep(img) {
        aspect-ratio: 640 / 639;
      }

      @include mq(false, $mq-sm - 1) {
        justify-content: flex-start;
        :deep(.xiaomi-stage__image) {
          width: 60%;
          max-width: 250px;
        }
      }
      @include mq($mq-sm) {
        bottom: 20%;
        left: 0;
        z-index: 1;
        width: 65%;
      }
    }
    &--redmiBuds5 {
      :deep(img) {
        aspect-ratio: 1;
      }

      @include mq(false, $mq-sm - 1) {
        justify-content: flex-end;
        margin-top: 2 * $grid-base;
        :deep(.xiaomi-stage__image) {
          width: 43%;
          max-width: 172px;
        }
      }
      @include mq($mq-sm) {
        bottom: -5%;
        left: 30%;
        z-index: 3;
        width: 26%;
      }
    }
    &--airFryer {
      :deep(img) {
        aspect-ratio: 311 / 347;
      }

      @include mq(false, $mq-sm - 1) {
        :deep(.xiaomi-stage__image) {
          width: 50%;
          max-width: 200px;
        }
      }
      @include mq($mq-sm) {
        bottom: 5%;
        left: 45%;
        z-index: 2;
        width: 38%;
      }
    }
    &--watchPro {
      :deep(img) {
        aspect-ratio: 496 / 511;
      }

      @include mq(false, $mq-sm - 1) {
        justify-content: flex-end;
        :deep(.xiaomi-stage__image) {
          width: 50%;
          max-width: 200px;
        }
      }
      @include mq($mq-sm) {
        bottom: -5%;
        width: 30%;
        left: 65%;
        z-index: 3;
      }
    }
  }
  // Position Icon
  &__plus-icon {
    z-index: 3;
    &--scooterLite4 {
      left: 35%;
      top: 10%;
      @include mq($mq-sm) {
        left: 35%;
        top: 5%;
      }
    }
    &--redmiBuds5 {
      left: -20%;
      top: 50%;
      @include mq($mq-sm) {
        top: -15%;
        left: 10%;
      }
    }
    &--airFryer {
      top: 20%;
      left: 80%;
      @include mq($mq-sm) {
        top: -13%;
        left: 60%;
      }
    }
    &--watchPro {
      top: -20%;
      right: 10%;

      @include mq($mq-sm) {
        top: -17%;
        right: 10%;
      }
    }
  }
}
</style>
